import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext"

const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    image: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const { user } = useAuth();
  const navigate = useNavigate();

  // État pour la recherche
  const [searchTerm, setSearchTerm] = useState("");

  // État pour la pagination avancée
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // État pour la vue responsive
  const [isMobileView, setIsMobileView] = useState(false);

  // Vérifier l'authentification
  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: { pathname: "/dashboard-ecobag-secure/products" } } });
    }
  }, [user, navigate]);

  // Vérifier la taille de l'écran pour déterminer la vue mobile/desktop
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
      setItemsPerPage(window.innerWidth < 768 ? 5 : 10);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Fonction optimisée pour chercher et filtrer des produits
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
      setTotalItems(products.length);
    } else {
      const lowercasedValue = searchTerm.toLowerCase();
      const results = products.filter(
        (product) =>
          product.nom.toLowerCase().includes(lowercasedValue) ||
          (product.description &&
            product.description.toLowerCase().includes(lowercasedValue))
      );
      setFilteredProducts(results);
      setTotalItems(results.length);
    }
  }, [searchTerm, products]);


  // Charger les produits avec pagination côté serveur
  const fetchProducts = useCallback(
    async (page = 1, limit = itemsPerPage, search = "") => {
      try {
        setLoading(true);
        // Pour l'exemple, on continue avec la pagination côté client
        const response = await axios.get("https://api.greenecobagsusa.com/api/products");

        // Correction pour traiter correctement les images en base64
        if (response.data && response.data.data) {
          // Aucune transformation nécessaire - utiliser directement la base64
          // Si l'API renvoie déjà des données en base64, on les utilise directement
          const processedProducts = response.data.data.map(product => ({
            ...product,
            // On suppose que l'image est déjà en base64 dans le champ image_base64
            // Aucune transformation nécessaire
          }));
          setProducts(processedProducts);
          setTotalItems(processedProducts.length);
        } else {
          setProducts([]);
          setTotalItems(0);
        }
        setError("");
      } catch (err) {
        console.error("Erreur lors du chargement des produits:", err);
        setError(
          "Impossible de charger les produits. Veuillez réessayer plus tard."
        );
      } finally {
        setLoading(false);
      }
    },
    [itemsPerPage]
  );

  // Charger les produits initiaux
  useEffect(() => {
    fetchProducts(currentPage, itemsPerPage, searchTerm);
  }, [fetchProducts, currentPage, itemsPerPage, searchTerm]);

  // Mettre à jour le nombre total de pages
  useEffect(() => {
    setTotalPages(Math.ceil(totalItems / itemsPerPage));

    if (
      currentPage > Math.ceil(totalItems / itemsPerPage) &&
      Math.ceil(totalItems / itemsPerPage) > 0
    ) {
      setCurrentPage(Math.ceil(totalItems / itemsPerPage));
    }
  }, [totalItems, itemsPerPage, currentPage]);

  // Obtenir les produits pour la page actuelle
  const getCurrentProducts = () => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Pagination - changement de page
  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);

      // Pour une vraie implémentation avec 1M+ produits:
      // fetchProducts(pageNumber, itemsPerPage, searchTerm);
    }
  };

  // Changer le nombre d'éléments par page
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Retourner à la première page lors du changement

    // Pour une vraie implémentation avec 1M+ produits:
    // fetchProducts(1, newItemsPerPage, searchTerm);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      setFormData({
        ...formData,
        image: files[0],
      });

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      fileReader.readAsDataURL(files[0]);
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  const openModal = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        nom: product.nom,
        description: product.description || "",
        image: null,
      });
      // Utiliser directement la base64 pour l'aperçu
      setPreviewImage(product.image_base64 || null);
    } else {
      setSelectedProduct(null);
      setFormData({
        nom: "",
        description: "",
        image: null,
      });
      setPreviewImage(null);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setFormData({
      nom: "",
      description: "",
      image: null,
    });
    setPreviewImage(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("nom", formData.nom);
      formDataToSend.append("description", formData.description);

      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      let response;

      if (selectedProduct) {
        formDataToSend.append("_method", "PUT");
        response = await axios.post(
          `https://api.greenecobagsusa.com/api/products/${selectedProduct.id}`,
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        response = await axios.post(
          "https://api.greenecobagsusa.com/api/products",
          formDataToSend,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      closeModal();
      fetchProducts(currentPage, itemsPerPage, searchTerm);
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);

      if (error.response && error.response.status === 422) {
        setFormErrors(error.response.data.errors || {});
      } else {
        setError(
          "Une erreur est survenue lors de l'enregistrement du produit."
        );
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce produit?")) {
      try {
        await axios.delete(`https://api.greenecobagsusa.com/api/products/${id}`);
        fetchProducts(currentPage, itemsPerPage, searchTerm);
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        setError(
          "Impossible de supprimer le produit. Veuillez réessayer plus tard."
        );
      }
    }
  };

  // Composant de pagination avancée pour gérer de grandes quantités de données
  const AdvancedPagination = () => {
    const maxPagesToShow = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex flex-col gap-4 mt-6 mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center text-sm text-gray-700 flex-wrap gap-2">
            <span>
              Display of {Math.min(totalItems, (currentPage - 1) * itemsPerPage + 1)} to{" "}
              {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} products
            </span>
          </div>

          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="border border-gray-300 rounded-md text-sm px-3 py-1.5 focus:outline-none focus:ring-green-500 focus:border-green-500"
          >
            <option value="5">5 </option>
            <option value="10">10 </option>
            <option value="25">25 </option>
            <option value="50">50 </option>
            <option value="100">100 </option>
          </select>
        </div>

        <div className="w-full overflow-x-auto">
          <div className="flex justify-end items-center gap-1 min-w-max px-2 py-2">
            {/* Bouton « */}
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded-l-md ${currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
                }`}
            >
              «
            </button>

            {/* Bouton < */}
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-3 py-1 ${currentPage === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
                }`}
            >
              <FaChevronLeft />
            </button>

            {/* Pages */}
            <div className="flex gap-1">
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => paginate(page)}
                  className={`px-3 py-1 rounded ${currentPage === page
                    ? "bg-green-700 text-white"
                    : "bg-green-600 text-white hover:bg-green-700"
                    }`}
                >
                  {page}
                </button>
              ))}
            </div>

            {/* Bouton > */}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 ${currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
                }`}
            >
              <FaChevronRight />
            </button>

            {/* Bouton » */}
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded-r-md ${currentPage === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-green-600 text-white hover:bg-green-700"
                }`}
            >
              »
            </button>
          </div>
        </div>

      </div>
    );
  };

  // Barre de recherche en haut
  // const SearchBar = () => {
  //   return (
  //     <div className="mb-6">
  //       <div className="relative">
  //         <div className="absolute inset-y-0 left-0 pl-1 flex items-center pointer-events-none">
  //           <FaSearch className="text-gray-400" />
  //         </div>
  //         <input
  //           type="text"
  //           placeholder="Search for products..."
  //           value={searchTerm}
  //           onChange={(e) => handleSearch(e.target.value)}
  //           className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white 
  //         placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 
  //         focus:ring-green-500 focus:border-green-500 sm:text-sm"
  //         />
  //       </div>
  //     </div>
  //   );
  // };


  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🛍️ Gestion des produits</h1>
          <p className="text-sm text-gray-500">Ajoutez, modifiez ou supprimez vos produits</p>
        </div>
        <button
          onClick={() => openModal()}
          className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg shadow flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
          </svg>
          Ajouter un produit
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="relative mb-6">
        <input
          type="text"
          placeholder="🔍 Rechercher un produit..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500"
        />
        <FaSearch className="absolute right-3 top-3 text-gray-400" />
      </div>

      {/* Vue Desktop */}
      <div className={`${isMobileView ? "hidden" : "block"} overflow-x-auto`}>
        <table className="w-full text-sm text-left text-gray-700 border rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3 text-center">Image</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getCurrentProducts().map(product => (
              <tr key={product.id} className="hover:bg-gray-50 transition">
                <td className="px-4 py-3 font-medium">{product.nom}</td>
                <td className="px-4 py-3 text-center">
                  {product.image_base64 ? (
                    <img src={product.image_base64} alt={product.nom} className="h-12 w-12 object-cover rounded" />
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded flex items-center justify-center text-gray-400">📦</div>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500">{product.description || "Aucune description"}</td>
                <td className="px-4 py-3 text-center flex justify-center gap-3">
                  <button onClick={() => openModal(product)} className="text-blue-600 hover:text-blue-800 transition"><FaEdit /></button>
                  <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-800 transition"><FaTrash /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredProducts.length > 0 && <AdvancedPagination />}
      </div>

      {/* Vue Mobile */}
      <div className={`${isMobileView ? "block" : "hidden"} grid grid-cols-1 gap-4`}>
        {getCurrentProducts().map(product => (
          <div key={product.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-semibold text-gray-800">{product.nom}</h3>
              <div className="flex gap-2">
                <button onClick={() => openModal(product)} className="text-blue-600 hover:text-blue-800"><FaEdit /></button>
                <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-800"><FaTrash /></button>
              </div>
            </div>
            <div className="flex justify-center mb-3">
              {product.image_base64 ? (
                <img src={product.image_base64} alt={product.nom} className="h-24 w-24 object-cover rounded" />
              ) : (
                <div className="h-24 w-24 bg-gray-200 rounded flex items-center justify-center text-gray-400">📦</div>
              )}
            </div>
            <p className="text-sm text-gray-600">{product.description || "Aucune description"}</p>
          </div>
        ))}
        {filteredProducts.length > 0 && <AdvancedPagination />}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6 relative">
            {/* Header */}
            <div className="mb-6 border-b pb-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {selectedProduct ? "✏️ Modifier le produit" : "➕ Ajouter un produit"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-red-500 transition"
              >
                ✖
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du produit</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${formErrors.nom ? "border-red-300" : "border-gray-300"} rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500`}
                />
                {formErrors.nom && <p className="mt-1 text-sm text-red-600">{formErrors.nom[0]}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  rows="3"
                  value={formData.description}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${formErrors.description ? "border-red-300" : "border-gray-300"} rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500`}
                ></textarea>
                {formErrors.description && <p className="mt-1 text-sm text-red-600">{formErrors.description[0]}</p>}
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image du produit</label>
                <input
                  type="file"
                  name="image"
                  accept="image/*"
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border ${formErrors.image ? "border-red-300" : "border-gray-300"} rounded-lg shadow-sm focus:ring-green-500 focus:border-green-500`}
                />
                {formErrors.image && <p className="mt-1 text-sm text-red-600">{formErrors.image[0]}</p>}
                {previewImage && (
                  <div className="mt-3">
                    <img src={previewImage} alt="Aperçu" className="h-32 w-auto object-cover rounded-lg border" />
                  </div>
                )}
              </div>

              {/* Boutons */}
              <div className="flex justify-end gap-3 pt-4 border-t mt-6">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition"
                >
                  {selectedProduct ? "💾 Mettre à jour" : "🛠️ Créer"}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-300 transition"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>


  );
};

export default ProductsManagement;
